'use client';

import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useErrorDialog } from '@/shared/components/ui/ErrorDialog';
import { useAuth } from '@/shared/contexts/AuthContext';
import { googleLogin } from '@/shared/services/authServices';
import { REFERRAL_STORAGE_KEY } from '@/shared/services/referralsService';
import { renderGoogleButton, isGoogleEnabled } from '@/shared/utils/google';
import TermsDialog from '@/shared/components/TermsDialog';
import { TermsCheck } from './CadastroParts';

type Step = 'button' | 'setup';

interface GoogleInfo {
  idToken: string;
  email: string;
  suggestedUsername: string;
  name?: string;
}

export default function CadastroGoogle() {
  const navigate = useNavigate();
  const { showError } = useErrorDialog();
  const { login } = useAuth();
  const btnRef = useRef<HTMLDivElement>(null);
  const renderedRef = useRef(false); // evita re-render do botao (StrictMode)

  const [step, setStep] = useState<Step>('button');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showTerms, setShowTerms] = useState(false);

  // Step 2 — dados do Google + form de setup
  const [googleInfo, setGoogleInfo] = useState<GoogleInfo | null>(null);
  const [loginId, setLoginId] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [acceptTerms, setAcceptTerms] = useState(false);

  // Handler do credential do Google — extraido pra ser estavel
  const onCredential = async (credential: string) => {
    setLoading(true);
    setError(null);
    try {
      const res: any = await googleLogin({ IdToken: credential });
      const stat = res?.status || res?.Status;

      // Caso A: conta nova — precisa setup de ID/senha
      if (stat === 'NEEDS_SETUP') {
        setGoogleInfo({
          idToken: credential,
          email: res.email,
          suggestedUsername: res.suggestedUsername,
          name: res.name,
        });
        setLoginId(res.suggestedUsername || '');
        setStep('setup');
        return;
      }

      // Caso B: login bem-sucedido (conta ja existe)
      if (stat === 'OK' && res?.token) {
        login({
          username: res.username || '',
          token: res.token,
          expiration: res.expiration,
        });
        navigate('/dashboard');
        return;
      }

      const msg = res?.message || res?.Message || 'Nao foi possivel entrar com Google.';
      setError(msg);
      showError({ title: 'Falha no login Google', message: msg });
    } catch (e: any) {
      const msg = e?.Message || e?.message || 'Nao foi possivel entrar com Google.';
      setError(msg);
      showError({ title: 'Falha no login Google', message: msg });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (step !== 'button') return;
    if (!isGoogleEnabled()) {
      setError('Login com Google nao esta configurado neste servidor.');
      return;
    }
    if (!btnRef.current || renderedRef.current) return;

    renderedRef.current = true;
    renderGoogleButton(btnRef.current, onCredential).catch((e) => {
      renderedRef.current = false;
      setError(e?.message || 'Nao foi possivel carregar o Google Sign-In.');
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step]);

  const validateSetup = (): string | null => {
    const id = loginId.trim();
    if (id.length < 7) return 'ID muito curto (mín. 7 caracteres).';
    if (id.length > 16) return 'ID muito longo (máx. 16 caracteres).';
    if (!/^[a-zA-Z0-9_]+$/.test(id)) return 'ID só pode conter letras, números e underline.';
    if (password.length < 6) return 'Senha muito curta (mín. 6 caracteres).';
    if (password !== confirmPassword) return 'As senhas não conferem.';
    if (!acceptTerms) return 'Você precisa aceitar os termos para continuar.';
    return null;
  };

  const submitSetup = async () => {
    setError(null);
    const err = validateSetup();
    if (err) { setError(err); return; }
    if (!googleInfo) return;

    setLoading(true);
    try {
      const res: any = await googleLogin({
        IdToken: googleInfo.idToken,
        Username: loginId.trim(),
        Password: password,
        ReferCode: (localStorage.getItem(REFERRAL_STORAGE_KEY) || '').trim() || null,
      });
      const stat = res?.status || res?.Status;
      if (stat === 'OK' && res?.token) {
        try { localStorage.removeItem(REFERRAL_STORAGE_KEY); } catch { /* */ }
        login({
          username: res.username || loginId.trim(),
          token: res.token,
          expiration: res.expiration,
        });
        navigate('/dashboard');
        return;
      }
      const msg = res?.message || res?.Message || 'Nao foi possivel criar conta.';
      setError(msg);
      showError({ title: 'Falha ao criar conta', message: msg });
    } catch (e: any) {
      const msg = e?.Message || e?.message || 'Nao foi possivel criar conta.';
      setError(msg);
      showError({ title: 'Falha ao criar conta', message: msg });
    } finally {
      setLoading(false);
    }
  };

  // === STEP 2: setup de ID + senha ===
  if (step === 'setup' && googleInfo) {
    return (
      <form onSubmit={(e) => { e.preventDefault(); submitSetup(); }}>
        <div className="auth-note">
          <strong>Google validado ✓</strong>
          <b>{googleInfo.email}</b>{googleInfo.name && <> · {googleInfo.name}</>}
          <br />
          Agora escolha o ID e a senha do jogo — você usa eles para logar no client do Cabal.
          No site, pode entrar tanto com esse ID/senha quanto pelo botão Google.
        </div>

        <label>ID do Jogo<input value={loginId} onChange={(e) => setLoginId(e.target.value)} placeholder="Seu ID (7-16 caracteres)" maxLength={16} /></label>
        <div className="form-row">
          <label>Senha<input type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" /></label>
          <label>Confirmar senha<input type="password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} placeholder="••••••••" /></label>
        </div>
        <TermsCheck checked={acceptTerms} onChange={setAcceptTerms} onOpenTerms={() => setShowTerms(true)} />

        {error && <div className="auth-error">{error}</div>}

        <button className="btn btn-gold submit-btn" type="submit" disabled={loading}>
          {loading ? 'Criando...' : 'Criar conta'}
        </button>
        <div className="auth-links center">
          <button type="button" onClick={() => { setStep('button'); setGoogleInfo(null); renderedRef.current = false; }}>
            ← Usar outra conta Google
          </button>
        </div>

        <TermsDialog open={showTerms} onClose={() => setShowTerms(false)} />
      </form>
    );
  }

  // === STEP 1: botao Google ===
  return (
    <div>
      <div className="auth-note">
        <strong>Entrar com Google</strong>
        Use sua conta Google para validar seu cadastro. Depois você escolhe o ID e a senha
        que vai usar para entrar no jogo.
      </div>

      <div className="google-slot">
        {loading ? <div className="auth-note">Validando com Google...</div> : <div ref={btnRef} />}
      </div>

      {error && <div className="auth-error">{error}</div>}

      <div className="auth-links center">Ao continuar, você aceita os termos de uso e a política de privacidade.</div>
    </div>
  );
}
